import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Pin, Clock, User, Bot, Folder } from 'lucide-react';
import type { Document } from '../../types';
import { parseUTCTimestamp } from '../../utils/dateUtils';

interface DocumentViewerProps {
  document: Document;
}

function formatDate(isoString: string): string {
  const date = parseUTCTimestamp(isoString);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({ document }) => {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 flex-1">
          {document.is_pinned && <Pin className="w-3.5 h-3.5 inline mr-1 text-amber-500" />}
          {document.title}
        </h3>
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
        <span className="flex items-center gap-1">
          {document.owner === 'agent' ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}
          {document.owner === 'agent' ? 'Agent' : 'You'}
        </span>
        {document.agent_edited && document.owner === 'user' && (
          <span className="text-amber-600 dark:text-amber-400">Agent edited</span>
        )}
        {document.folder && (
          <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[11px]">
            <Folder className="w-3 h-3" />
            {document.folder}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDate(document.updated_time)}
        </span>
      </div>

      <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{document.body}</ReactMarkdown>
      </div>
    </div>
  );
};
